import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { PayPalEcStatesEnum } from 'i2g-ng-payment';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { NavigationService } from 'i2g-ng-utils';
import { List } from 'immutable';
import { isBoolean } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { shareReplay, take, takeUntil } from 'rxjs/operators';
import { DialogService } from '../../../dialog/dialog.service';
import { DocumentTypesEnum } from '../../../document-data/document-data.data';
import {
  TOGGLE_CARD_PAYMENTS_DIALOG_TYPE,
  TOGGLE_DIRECT_TRANSFER_DIALOG_TYPE,
  TOGGLE_I2G_MONEY_DIALOG_TYPE,
  TOGGLE_PAYPAL_DIALOG_TYPE,
} from '../../../document-detail/components/toggle-payment-method-dialog/toggle-payment-method-dialog.component';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { LocalizationService } from '../../../localization/localization.service';
import {
  I2gMoneyApplicationStatusEnum,
  I2gMoneyContext,
} from '../../../settings-data/i2g-money.data';
import { SettingsDataDispatcher } from '../../../settings-data/settings-data.dispatcher';
import { AchDebit } from '../../../settings-data/sub-models/company-settings/ach-debit.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import {
  IPaymentOptionsAdditionalProperties,
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { VerifyAccountDispatcher } from '../../../verify-account/verify-account.dispatcher';
import { DIALOG_TYPE as bankTransferInfoDialogType } from '../bank-transfer-info-dialog/bank-transfer-info-dialog.component';
import { DIALOG_TYPE as cardPaymentsInfoDialogType } from '../card-payments-info-dialog/card-payments-info-dialog.component';
// eslint-disable-next-line max-len
import { DIALOG_TYPE as enableStripeVerificationDialogType } from '../enable-stripe-verification-dialog/enable-stripe-verification-dialog.component';
import { DIALOG_TYPE as paypalInfoDialogType } from '../paypal-info-dialog/paypal-info-dialog.component';
import { PaymentOptionsBuilder } from './payment-options.builder';
import { UpdatePaymentMethodParams } from './payment-options.interface';
import { PAYPAL_DASHBOARD_LINK } from '../../../payments-data/payments-data.data';
import { PaymentsDataBuilder } from '../../../payments-data/payments-data.builder';
import { SETTINGS_MASTER_ROUTE } from '../../../main/main.data';
import { SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE } from '../../../settings/settings.data';
import { URLS } from '../../../shared/constants/urls.constant';

@Component({
  selector: 'app-payment-options',
  templateUrl: './payment-options.component.html',
  styleUrls: ['./payment-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [PaymentOptionsBuilder, PaymentsDataBuilder],
})
export class PaymentOptionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() showI2gMoney: boolean;
  @Input() showBankTransfer: boolean;
  @Input() showCardPayments: boolean;
  @Input() showPaypalEc: boolean;
  @Input() preventDisableOptions: boolean;
  @Input() i2gMoneyEnabled: boolean;
  @Input() i2gMoneyContext: I2gMoneyContext;
  @Input() bankTransferEnabled: boolean;
  @Input() cardPaymentsEnabled: boolean;
  @Input() paypalEcEnabled: boolean;
  @Input() paypalEcStatus: PayPalEcStatesEnum;
  @Input() achDebit: AchDebit;
  @Input() cardFeesDescription: string;
  @Input() underwritingUrl: string;
  @Input() availableCardTypes: List<string>;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input()
  paymentOptionsTrackingProperties: IPaymentOptionsAdditionalProperties;
  @Input() i2gMoneyTrackingIdentifier = 'accept_invoice2go_money';
  @Input() bankTransferTrackingIdentifier = 'accept_bank_transfer';
  @Input() cardPaymentsTrackingIdentifier = 'accept_credit_cards';
  @Input() paypalTrackingIdentifier = 'accept_paypal_ec';
  @Input() companyCountry: string;
  @Input() documentType: DocumentTypesEnum;

  @Output()
  updateI2gMoney: EventEmitter<UpdatePaymentMethodParams> = new EventEmitter<UpdatePaymentMethodParams>();
  @Output()
  updateBankTransfer: EventEmitter<UpdatePaymentMethodParams> = new EventEmitter<UpdatePaymentMethodParams>();
  @Output()
  updateCardPayments: EventEmitter<UpdatePaymentMethodParams> = new EventEmitter<UpdatePaymentMethodParams>();
  @Output()
  updatePaypal: EventEmitter<UpdatePaymentMethodParams> = new EventEmitter<UpdatePaymentMethodParams>();
  @Output()
  confirmSetupBankTransferDialog: EventEmitter<void> = new EventEmitter<void>();

  _i2gMoneyEnabledField: AbstractControl;
  private _bankTransferEnabledField: AbstractControl;
  private _cardPaymentsEnabledField: AbstractControl;
  private _paypalEcEnabledField: AbstractControl;
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  locale: string;
  bankTransferDescription: string;
  formKeys: any;
  form: FormGroup;
  infoIcon: IIcon;
  IconSizesEnum: typeof IconSizesEnum;
  availablePaypalLogos: List<string>;
  formControl: AbstractControl;
  i2gMoneyInfoAction: ITrackingAction;
  bankTransferInfoAction: ITrackingAction;
  cardPaymentsInfoAction: ITrackingAction;
  paypalInfoAction: ITrackingAction;
  isAccountVerified$: Observable<boolean>;
  isDocumentAnEstimate: boolean;
  i2gMoneyIsDisabled: boolean;
  i2gMoneyApplicationStatusEnum: typeof I2gMoneyApplicationStatusEnum;

  // paypal
  isPayPalToggleDisabled: boolean;
  showPayPalToggle: boolean;
  paypalEcStatesEnum: typeof PayPalEcStatesEnum;
  payPalWarningTrackingAction: ITrackingAction;

  // For toggling payment methods
  toggleI2gMoneyDialogType = TOGGLE_I2G_MONEY_DIALOG_TYPE;
  toggleCardPaymentsDialogType = TOGGLE_CARD_PAYMENTS_DIALOG_TYPE;
  togglePayPalDialogType = TOGGLE_PAYPAL_DIALOG_TYPE;
  toggleDirectTransferDialogType = TOGGLE_DIRECT_TRANSFER_DIALOG_TYPE;

  // For tracking payment method toggles
  toggleI2gMoneyPrimaryAction: ITrackingAction;
  toggleI2gMoneySecondaryAction: ITrackingAction;
  togglePaypalPrimaryAction: ITrackingAction;
  togglePaypalSecondaryAction: ITrackingAction;
  toggleStripePrimaryAction: ITrackingAction;
  toggleStripeSecondaryAction: ITrackingAction;
  toggleDirectTransferPrimaryAction: ITrackingAction;
  toggleDirectTransferSecondaryAction: ITrackingAction;
  trackingDialogName = 'toggle_payment_method';

  constructor(
    private _paymentOptionsBuilder: PaymentOptionsBuilder,
    private _paymentsDataBuilder: PaymentsDataBuilder,
    private _localizationService: LocalizationService,
    private _dialogService: DialogService,
    private _featuresDataStore: FeaturesDataStore,
    private _trackingDispatcher: TrackingDispatcher,
    private _verifyAccountDispatcher: VerifyAccountDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _navigationService: NavigationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.isDocumentAnEstimate =
      this.documentType === DocumentTypesEnum.estimate;

    if (changes.achDebit && this.achDebit) {
      this.bankTransferDescription = this._paymentOptionsBuilder.buildBankTransferDescription(
        this.achDebit,
        this._localizationService.getLocale()
      );
    }

    if (changes.companyCountry && this.companyCountry) {
      this.availablePaypalLogos = this._paymentOptionsBuilder.buildPaypalLogos(
        this.companyCountry
      );
    }

    // updating if I2g Money checkbox is available
    if (changes.i2gMoneyContext && changes.i2gMoneyContext.currentValue) {
      this.i2gMoneyIsDisabled = !changes.i2gMoneyContext.currentValue.settings
        .allowed_by_partner;

      // if just enabled, set document settings to true
      if (changes.i2gMoneyContext.previousValue) {
        const {
          allowed_by_partner,
          completed_onboarding,
          enabled_by_user,
        } = changes.i2gMoneyContext
          .previousValue as I2gMoneyContext['settings'];
        const {
          allowed_by_partner: current_allowed_by_partner,
          completed_onboarding: current_onboarding_completed,
          enabled_by_user: current_enabled_by_user,
        } = changes.i2gMoneyContext.currentValue as I2gMoneyContext['settings'];

        const wasEnabledPreviously =
          allowed_by_partner && completed_onboarding && enabled_by_user;
        const isEnabledCurrently =
          current_allowed_by_partner &&
          current_onboarding_completed &&
          current_enabled_by_user;

        if (isEnabledCurrently && !wasEnabledPreviously) {
          if (this.form) {
            this._i2gMoneyEnabledField.setValue(true);
          }
        }
      }
    }

    if (this.form) {
      if (this.i2gMoneyEnabled) {
        this._i2gMoneyEnabledField.setValue(this.i2gMoneyEnabled);
      }

      if (this.bankTransferEnabled) {
        this._bankTransferEnabledField.setValue(this.bankTransferEnabled);
      }

      if (this.cardPaymentsEnabled) {
        this._cardPaymentsEnabledField.setValue(this.cardPaymentsEnabled);
      }

      if (this.paypalEcEnabled) {
        this._paypalEcEnabledField.setValue(this.paypalEcEnabled);
      }
    }
  }

  ngOnInit() {
    this.IconSizesEnum = IconSizesEnum;
    this.locale = this._localizationService.getLocale();

    // I2g Money
    this.i2gMoneyApplicationStatusEnum = I2gMoneyApplicationStatusEnum;

    // PayPal
    this.paypalEcStatesEnum = PayPalEcStatesEnum;
    this.isPayPalToggleDisabled =
      this.preventDisableOptions && this.paypalEcEnabled;
    this.showPayPalToggle = [
      PayPalEcStatesEnum.underwriting_required,
      PayPalEcStatesEnum.completed,
      PayPalEcStatesEnum.unlinked,
    ].includes(this.paypalEcStatus);

    this.formKeys = this._paymentOptionsBuilder.FORM_KEYS;
    this.form = this._paymentOptionsBuilder.buildForm(
      this.bankTransferEnabled,
      this.cardPaymentsEnabled,
      this.paypalEcEnabled,
      this.bankTransferEnabled
    );

    this.infoIcon = {
      name: 'info',
      size: IconSizesEnum.medium,
    };
    this.cardPaymentsInfoAction = this._trackingActionBuilder.buildButtonTapped(
      'info_icon_credit_card',
      this.trackingScreenName
    );
    this.paypalInfoAction = this._trackingActionBuilder.buildButtonTapped(
      'info_icon_paypal',
      this.trackingScreenName
    );
    this.payPalWarningTrackingAction = this._paymentsDataBuilder.buildPayPalWarningTracking(
      this.paypalEcStatus,
      this.trackingScreenName
    );

    this.bankTransferInfoAction = this._trackingActionBuilder.buildButtonTapped(
      'info_icon_bank_transfer',
      this.trackingScreenName
    );
    this.i2gMoneyInfoAction = this._trackingActionBuilder.buildButtonTapped(
      'info_icon_invoice2go_money',
      this.trackingScreenName
    );

    this._i2gMoneyEnabledField = this.form.get(this.formKeys.I2G_MONEY_ENABLED);
    this._bankTransferEnabledField = this.form.get(
      this.formKeys.BANK_TRANSFER_ENABLED
    );
    this._cardPaymentsEnabledField = this.form.get(
      this.formKeys.CARD_PAYMENTS_ENABLED
    );
    this._paypalEcEnabledField = this.form.get(this.formKeys.PAYPAL_EC_ENABLED);
    this.isAccountVerified$ = this._featuresDataStore.isAccountVerified$.pipe(
      takeUntil(this._destroyed),
      shareReplay(1)
    );

    this.isAccountVerified$.subscribe((isAccountVerified) => {
      if (!isAccountVerified) {
        this.form.get(this.formKeys.CARD_PAYMENTS_ENABLED).disable();
      }
    });

    this.toggleI2gMoneyPrimaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_invoice2go_money_globally',
      this.trackingScreenName
    );
    this.toggleI2gMoneySecondaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_invoice2go_money_this_once',
      this.trackingScreenName
    );
    this.togglePaypalPrimaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_paypal_globally',
      this.trackingScreenName
    );
    this.togglePaypalSecondaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_paypal_this_once',
      this.trackingScreenName
    );
    this.toggleStripePrimaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_stripe_globally',
      this.trackingScreenName
    );
    this.toggleStripeSecondaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_stripe_this_once',
      this.trackingScreenName
    );
    this.toggleDirectTransferPrimaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_stripe_ach_globally',
      this.trackingScreenName
    );
    this.toggleDirectTransferSecondaryAction = this._trackingActionBuilder.buildButtonTapped(
      'turn_off_stripe_ach_this_once',
      this.trackingScreenName
    );
  }

  toggleI2gMoney(event: Event) {
    // prevent checkbox set its value before settings store is updated
    event.stopPropagation();
    event.preventDefault();

    const value = this._i2gMoneyEnabledField.value;
    if (value) {
      this._dialogService.openDialog({
        type: this.toggleI2gMoneyDialogType,
      });
    } else {
      this.toggleI2gMoneyFor(true);
    }
  }

  toggleBankTransfer(event: Event) {
    // prevent checkbox set its value before settings store is updated
    event.stopPropagation();
    event.preventDefault();

    if (this._bankTransferEnabledField.value) {
      // update dialog copy
      this._dialogService.openDialog({
        type: this.toggleDirectTransferDialogType,
      });
    } else {
      this.toggleDirectTransferFor(true);
    }
  }

  toggleCardPayments(event: Event) {
    // prevent checkbox set its value before settings store is updated
    event.stopPropagation();
    event.preventDefault();

    if (this._cardPaymentsEnabledField.value) {
      this._dialogService.openDialog({
        type: this.toggleCardPaymentsDialogType,
      });
    } else {
      this.toggleCardPaymentsFor(true);
    }
  }

  togglePaypal(event: Event) {
    // prevent checkbox set its value before settings store is updated
    event.stopPropagation();
    event.preventDefault();

    if (this._paypalEcEnabledField.value) {
      this._dialogService.openDialog({
        type: this.togglePayPalDialogType,
      });
    } else {
      this.togglePayPalFor(true);
    }
  }

  openI2gMoneyOnboardingDialog() {
    this._settingsDataDispatcher.showI2gMoneyOnboarding(
      this.trackingScreenName
    );
  }

  openBankTransferInfoDialog() {
    this._dialogService.openDialog({ type: bankTransferInfoDialogType });
  }

  openCardPaymentsInfoDialog() {
    this.isAccountVerified$.pipe(take(1)).subscribe((isAccountVerified) => {
      this._dialogService.openDialog({
        type: isAccountVerified
          ? cardPaymentsInfoDialogType
          : enableStripeVerificationDialogType,
      });
    });
  }

  openPaypalInfoDialog() {
    this._dialogService.openDialog({ type: paypalInfoDialogType });
  }

  onPayPalWarningAction() {
    switch (this.paypalEcStatus) {
      case PayPalEcStatesEnum.in_progress:
        this.togglePayPalFor(true);
        break;
      case PayPalEcStatesEnum.email_not_confirmed:
        this._navigationService.goToExternalUrl(
          URLS.PAYMENTS_HELP_PAYPAL_EMAIL_NOT_VERIFIED,
          true
        );
        break;
      case PayPalEcStatesEnum.restricted:
        this._navigationService.goToExternalUrl(PAYPAL_DASHBOARD_LINK, true);
        break;
      default:
        return false;
    }
  }

  /**
   * Instead of enabling payments we redirect users to PayPal,
   *
   * since: new paypal onboarding flow
   */
  turnOnPayments() {
    this._navigationService.goTo([
      SETTINGS_MASTER_ROUTE,
      SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE,
    ]);
  }

  onResendEmail() {
    this._verifyAccountDispatcher.resendVerificationEmail();
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this._trackingActionBuilder.buildButtonTapped(
        'resend_verification_email',
        this.trackingScreenName
      )
    );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();

    this._dialogService.closeDialog({ type: bankTransferInfoDialogType });
    this._dialogService.closeDialog({ type: cardPaymentsInfoDialogType });
    this._dialogService.closeDialog({ type: paypalInfoDialogType });
    this._dialogService.closeDialog({
      type: this.toggleCardPaymentsDialogType,
    });
    this._dialogService.closeDialog({ type: this.togglePayPalDialogType });
  }

  toggleI2gMoneyFor(accountLevel: boolean) {
    // update UI
    this._i2gMoneyEnabledField.setValue(!this._i2gMoneyEnabledField.value);

    // notify parent
    this.updateI2gMoney.emit({
      enabled: this._i2gMoneyEnabledField.value,
      atAccountLevel: accountLevel,
    });

    // close dialog
    this._dialogService.closeDialog({ type: this.toggleI2gMoneyDialogType });
  }

  toggleCardPaymentsFor(accountLevel: boolean) {
    // update UI
    this._cardPaymentsEnabledField.setValue(
      !this._cardPaymentsEnabledField.value
    );

    // notify parent
    this.updateCardPayments.emit({
      enabled: this._cardPaymentsEnabledField.value,
      atAccountLevel: accountLevel,
    });

    // close dialog
    this._dialogService.closeDialog({
      type: this.toggleCardPaymentsDialogType,
    });
  }

  togglePayPalFor(accountLevel: boolean) {
    // update UI
    this._paypalEcEnabledField.setValue(!this._paypalEcEnabledField.value);

    // notify parent
    this.updatePaypal.emit({
      enabled: this._paypalEcEnabledField.value,
      atAccountLevel: accountLevel,
    });

    // disable the toggle if underwriting is required
    // Note. This is not an ideal solution, unfortunately
    // making this cleaner would require changes to statement flow,
    // Since the adoption rate for statements is very low and its probably not worth our time.
    if (this.paypalEcStatus !== PayPalEcStatesEnum.completed) {
      this._paypalEcEnabledField.setValue(!this._paypalEcEnabledField.value);
    }

    // close dialog
    this._dialogService.closeDialog({ type: this.togglePayPalDialogType });
  }

  toggleDirectTransferFor(accountLevel: boolean) {
    // update UI
    this._bankTransferEnabledField.setValue(
      !this._bankTransferEnabledField.value
    );

    // notify parent
    this.updateBankTransfer.emit({
      enabled: this._bankTransferEnabledField.value,
      atAccountLevel: accountLevel,
    });

    // close dialog
    this._dialogService.closeDialog({
      type: this.toggleDirectTransferDialogType,
    });
  }
}
